import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FocusableSection } from '../components/Focusable';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../config';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
  padding-left: ${props => props.$sidebar ? 'calc(150px + 2rem)' : '2rem'};
  background: #fff;
  transition: padding-left 350ms;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 70px;
  }
`;

const TimeText = styled.h1`
  color: #000;
  font-size: 2.2rem;
  font-weight: 300;
  margin: 0 0 2rem 0;
  text-align: center;

  span {
    font-weight: 500;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const PhotoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  max-width: 900px;
  margin-bottom: 2rem;
`;

const Photo = styled.img`
  max-width: 100%;
  max-height: 50vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: opacity 0.5s ease;
  opacity: ${props => props.$loaded ? 1 : 0};

  @media (max-width: 768px) {
    max-height: 40vh;
    border-radius: 8px;
  }
`;

const PhotoPlaceholder = styled.div`
  color: #999;
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
`;

const RemindersSection = styled.div`
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ReminderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
  border-left: 3px solid ${props => props.$type === 'rdv' ? '#632ce4' : '#27ae60'};
`;

const ReminderIcon = styled.div`
  font-size: 1.5rem;
`;

const ReminderContent = styled.div`
  flex: 1;
`;

const ReminderTitle = styled.div`
  color: #000;
  font-weight: 600;
  font-size: 1rem;
`;

const ReminderTime = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

export const Home = ({ sidebar }) => {
  const { user } = useContext(AuthContext);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [photos, setPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(null);

  // Exemples de rappels (sera alimenté par l'agenda plus tard) - seulement 2
  const reminders = [
    {
      id: 1,
      type: 'rappel',
      title: 'Prendre ses medicaments',
      time: '17h00'
    },
    {
      id: 2,
      type: 'rdv',
      title: 'Visite infirmiere',
      time: '18h00'
    }
  ];

  const token = localStorage.getItem('token');

  // Mise à jour de l'heure toutes les secondes
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Charger une photo de manière sécurisée (avec token)
  const loadSecurePhoto = async (mediaId) => {
    try {
      const res = await fetch(`${API_URL}/api/media/stream/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erreur chargement photo');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      return url;
    } catch (err) {
      console.error('Erreur chargement photo:', err);
      return null;
    }
  };

  // Charger les photos
  useEffect(() => {
    const loadPhotos = async () => {
      if (!token) return;

      try {
        const res = await axios.get(`${API_URL}/api/media/received`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Filtrer uniquement les images
        const imagePhotos = res.data.filter(media =>
          media.file_type && media.file_type.startsWith('image/')
        );

        if (imagePhotos.length > 0) {
          // Mélanger les photos aléatoirement
          const shuffled = [...imagePhotos].sort(() => Math.random() - 0.5);
          setPhotos(shuffled);
          setCurrentPhotoIndex(0);
        }
      } catch (error) {
        console.error('Erreur chargement photos:', error);
      }
    };

    loadPhotos();
  }, [token]);

  // Charger la photo courante quand l'index change
  useEffect(() => {
    const loadCurrentPhoto = async () => {
      if (photos.length === 0) return;

      const photo = photos[currentPhotoIndex];
      if (photo) {
        setPhotoLoaded(false);
        // Révoquer l'ancienne URL blob si elle existe
        if (currentPhotoUrl) {
          URL.revokeObjectURL(currentPhotoUrl);
        }
        const url = await loadSecurePhoto(photo.id);
        setCurrentPhotoUrl(url);
      }
    };

    loadCurrentPhoto();
  }, [currentPhotoIndex, photos]);

  // Nettoyer l'URL blob au démontage
  useEffect(() => {
    return () => {
      if (currentPhotoUrl) {
        URL.revokeObjectURL(currentPhotoUrl);
      }
    };
  }, []);

  // Changer de photo toutes les 2 minutes
  useEffect(() => {
    if (photos.length <= 1) return;

    const photoTimer = setInterval(() => {
      setCurrentPhotoIndex(prev => (prev + 1) % photos.length);
    }, 120000); // 2 minutes

    return () => clearInterval(photoTimer);
  }, [photos.length]);

  const formatDateTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    const dateStr = date.toLocaleDateString('fr-FR', options);

    return {
      time: `${hours}h${minutes}`,
      date: dateStr.charAt(0).toUpperCase() + dateStr.slice(1)
    };
  };

  const { time, date } = formatDateTime(currentTime);
  const currentPhoto = photos[currentPhotoIndex];

  return (
    <FocusableSection focusKey="HOME_PAGE">
      <Container $sidebar={sidebar}>
        <TimeText>
          Il est <span>{time}</span>, {date}
        </TimeText>

        <PhotoContainer>
          {currentPhoto && currentPhotoUrl ? (
            <Photo
              src={currentPhotoUrl}
              alt="Photo de l'album"
              $loaded={photoLoaded}
              onLoad={() => setPhotoLoaded(true)}
            />
          ) : (
            <PhotoPlaceholder>
              {photos.length > 0 ? 'Chargement...' : 'Aucune photo dans l\'album'}
            </PhotoPlaceholder>
          )}
        </PhotoContainer>

        <RemindersSection>
          {reminders.map(reminder => (
            <ReminderItem key={reminder.id} $type={reminder.type}>
              <ReminderIcon>
                {reminder.type === 'rdv' ? '🗓️' : '⏰'}
              </ReminderIcon>
              <ReminderContent>
                <ReminderTitle>{reminder.title}</ReminderTitle>
                <ReminderTime>{reminder.time}</ReminderTime>
              </ReminderContent>
            </ReminderItem>
          ))}
        </RemindersSection>
      </Container>
    </FocusableSection>
  );
};

export default Home;
